import { reactive } from "vue";
import { apiUrl } from "@/utils/api";

// Centralized chat state and lazy initialization (similar pattern to walletState)
// Stores TalkJS session data from backend - matches Flutter's ChatTokenModel
export const chatState = reactive({
  // TalkJS session data (from /chat/token endpoint)
  appId: null,
  userId: null,
  userName: null,
  userEmail: null,
  userPhotoUrl: null,
  signature: null, // HMAC signature for TalkJS authentication
  role: null,

  // State flags
  loading: false,
  initialized: false,
  inFlight: null,
  error: null,
  lastInitAt: 0,

  // TalkJS SDK instance (set after initialization)
  session: null,
  me: null,

  // Shared reactive conversations list for real-time preview updates
  conversations: [],
  // Timestamp of last conversation list mutation (for watcher triggers)
  lastConversationUpdate: 0,
});

// Helpers to update shared conversation state reactively
export function upsertConversation(conversation) {
  if (!conversation) return;
  const id = conversation.talkjs_conversation_id || conversation.id;
  if (!id) return;
  const idx = chatState.conversations.findIndex(
    (c) => String(c.talkjs_conversation_id || c.id) === String(id)
  );
  if (idx !== -1) {
    chatState.conversations.splice(idx, 1, {
      ...chatState.conversations[idx],
      ...conversation,
    });
  } else {
    chatState.conversations.push(conversation);
  }
  chatState.lastConversationUpdate = Date.now();
}

export function updateLastMessage(conversationId, message) {
  if (!conversationId || !message) return;
  const idx = chatState.conversations.findIndex(
    (c) => String(c.talkjs_conversation_id || c.id) === String(conversationId)
  );
  if (idx === -1) return;

  const conversation = chatState.conversations[idx];
  const lastMessage = {
    id: message.id,
    text: message.text || message.content,
    senderId: message.senderId || message.sender_id,
    createdAt: message.createdAt || message.created_at || new Date().toISOString(),
    content: message.content,
  };

  chatState.conversations.splice(idx, 1, {
    ...conversation,
    lastMessage,
    last_message: lastMessage,
    unreadMessageCount: conversation.unreadMessageCount || 0,
    unread_count: conversation.unread_count || 0,
  });
  chatState.lastConversationUpdate = Date.now();
}

export function bumpConversationToTop(conversationId) {
  const idx = chatState.conversations.findIndex(
    (c) => String(c.talkjs_conversation_id || c.id) === String(conversationId)
  );
  if (idx > 0) {
    const [conversation] = chatState.conversations.splice(idx, 1);
    chatState.conversations.unshift(conversation);
    chatState.lastConversationUpdate = Date.now();
  }
}

function getToken() {
  try {
    return localStorage.getItem("access_token");
  } catch (_) {
    return null;
  }
}

function buildAuthHeaders() {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (
    typeof window !== "undefined" &&
    window.appRegion &&
    window.appRegion.phone_code
  ) {
    headers["x-country-code"] = window.appRegion.phone_code;
  }
  return headers;
}

export async function ensureChatInitialized() {
  if (chatState.initialized || chatState.inFlight) {
    return chatState.inFlight || Promise.resolve(true);
  }

  chatState.loading = true;
  chatState.error = null;

  const p = (async () => {
    try {
      const res = await fetch(`${apiUrl}/chat/token`, {
        method: "GET",
        headers: buildAuthHeaders(),
      });
      if (!res.ok) {
        chatState.initialized = false;
        resetChatState();
        return false;
      }
      const json = await res.json();
      const data = json && (json.data || json);

      // Store all TalkJS token data (matches Flutter's ChatTokenModel)
      chatState.appId = data.appId || data.app_id || null;
      chatState.userId = data.userId || data.user_id || null;
      chatState.userName = data.userName || data.user_name || data.name || null;
      chatState.userEmail =
        data.userEmail || data.user_email || data.email || null;
      chatState.userPhotoUrl =
        data.userPhotoUrl ||
        data.user_photo_url ||
        data.photo_url ||
        data.avatar ||
        null;
      chatState.signature = data.signature || null;
      chatState.role = data.role || "default";

      chatState.initialized = !!(chatState.userId && chatState.appId);
      chatState.lastInitAt = Date.now();

      console.log("[ChatStore] Initialized with:", {
        appId: chatState.appId,
        userId: chatState.userId,
        userName: chatState.userName,
        hasSignature: !!chatState.signature,
      });

      return chatState.initialized;
    } catch (e) {
      chatState.error = e?.message || "Failed to init chat";
      chatState.initialized = false;
      resetChatState();
      return false;
    } finally {
      chatState.loading = false;
      chatState.inFlight = null;
    }
  })();

  chatState.inFlight = p;
  return p;
}

function resetChatState() {
  chatState.appId = null;
  chatState.userId = null;
  chatState.userName = null;
  chatState.userEmail = null;
  chatState.userPhotoUrl = null;
  chatState.signature = null;
  chatState.role = null;
  chatState.session = null;
  chatState.me = null;
}

// Initialize TalkJS SDK session (like Flutter's Session creation)
export async function initTalkJSSession() {
  if (!chatState.initialized) {
    await ensureChatInitialized();
  }

  if (!chatState.appId || !chatState.userId) {
    throw new Error("Chat not initialized - missing appId or userId");
  }

  // Wait for TalkJS SDK to load
  await Talk.ready;

  // Create TalkJS session (like Flutter: Session(appId: token.appId))
  const session = new Talk.Session({
    appId: chatState.appId,
    signature: chatState.signature || undefined,
    me: new Talk.User({
      id: chatState.userId,
      name: chatState.userName || "User",
      email: chatState.userEmail ? [chatState.userEmail] : undefined,
      photoUrl: chatState.userPhotoUrl || undefined,
      role: chatState.role || "default",
    }),
  });

  chatState.session = session;
  chatState.me = session.me;

  console.log("[ChatStore] TalkJS session created");
  return session;
}

export function getAuthHeaders() {
  return buildAuthHeaders();
}
